import React, {createContext, useState, useContext, ReactNode, useEffect} from 'react';
import {useLocation, useNavigate} from "react-router-dom";
import {login as loginQuery, logout as logoutQuery} from "../queries/userQueries"
import {User, UserResponse, UserRole} from "../types/users";
import {verifyToken as verifyTokenQuery} from "../queries/userQueries"

// Define the shape of the context value
interface AuthContextProps {
    isAuthenticated: boolean;
    setAuthenticated: (userResponse: UserResponse | undefined) => void;
    login: (email: string, password: string) => void;
    logout: () => any;
    verifyToken: () => Promise<void>;
    user: User | undefined;
    checkUserRoles: (roles: UserRole[]) => boolean | undefined;
    updateLocalStorageUser: (user: User) => void
}

// Create the context with a default value
const AuthContext = createContext<AuthContextProps | undefined>(undefined);

// Create a provider component
const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [isAuthenticated, setAuthenticated] = useState<boolean>(false);
    const location = useLocation();
    const navigate = useNavigate();
    const [user, setUser] = useState<User>();

    useEffect(() => {
        const requestUrl = location.pathname
        const requestParams = new URLSearchParams(location.search);
        const checkAuthentication = async () => {
            const token = localStorage.getItem('token');
            const isAuthenticated = localStorage.getItem('isAuthenticated');
            const userString = localStorage.getItem('user');
            if(isAuthenticated === "true" && token && userString) {
                const user = JSON.parse(userString);
                setAuthenticated(true);
                setUser(user)
                navigate(`${requestUrl}?${requestParams}`);
            } else {
                setAuthenticated(false);
            }
        };

        checkAuthentication();
    }, []);

    const setAuthenticatedWrapper = (userResponse: UserResponse | undefined) => {
        const accessToken = userResponse ? userResponse.session_token : undefined;
        const user = userResponse ? userResponse.user : undefined;
        if(accessToken === undefined) {
            setAuthenticated(false);
            localStorage.setItem('isAuthenticated', "false");
        } else {

            setAuthenticated(true);
            localStorage.setItem('isAuthenticated', "true");
            setUser(user)
        }
        localStorage.setItem('user', user ? JSON.stringify(user) : '');
        localStorage.setItem('token', accessToken || '');
    };

    function checkUserRoles(roles: UserRole[]) {
        return user?.roles?.some(userRole =>
            roles.includes(userRole.name as UserRole)
        );
    }

    async function login(email: string, password: string){
        const loginResponse = await loginQuery(email, password);
        setAuthenticatedWrapper(loginResponse);
        navigate('/matchboard');
    }

    async function logout(){
        return await logoutQuery()
    }

    async function verifyToken(){
        return await verifyTokenQuery()
    }

    function updateLocalStorageUser (user: User) {
        localStorage.setItem('user', JSON.stringify(user));
    }



    return (
        <AuthContext.Provider value={{isAuthenticated, setAuthenticated : setAuthenticatedWrapper, login, user, logout, verifyToken, checkUserRoles, updateLocalStorageUser}}>
            {children}
        </AuthContext.Provider>
    );
};

const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within a AuthProvider');
    }
    return context;
};

export { AuthProvider, useAuth };